import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { SiteHeader } from './SiteHeader';
import { SiteFooter } from './SiteFooter';
import { formatProcedureName } from './portfolioContent';
import { formatCurrency } from '../utils/currency';
import './variables.css';
import './SiteScheduling.css';

function formatPhoneInput(val) {
  if (!val) return '';
  const digits = String(val).replace(/\D/g, '').slice(0, 11);
  if (digits.length === 0) return '';
  if (digits.length <= 2) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  if (digits.length <= 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  }
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7, 11)}`;
}

export function SiteScheduling({ onNavigate, initialServiceId = null, usuario }) {
  const [procedures, setProcedures] = useState([]);
  const [formData, setFormData] = useState({
    nome: '',
    telefone: '',
    servico_id: initialServiceId || '',
    data: new Date().toISOString().split('T')[0],
    hora: '',
    observacoes: '',
  });

  const [availableTimes, setAvailableTimes] = useState([]);
  const [timesLoading, setTimesLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [successDetails, setSuccessDetails] = useState(null);

  useEffect(() => {
    const loadProcedures = async () => {
      try {
        const response = await axios.get('/api/servicos/portfolio');
        const list = response.data || [];
        setProcedures(list);
        if (!formData.servico_id && list.length > 0) {
          setFormData((prev) => ({ ...prev, servico_id: list[0].id }));
        }
      } catch (err) {
        setErrors((prev) => ({ ...prev, submit: 'Erro ao carregar lista de procedimentos.' }));
      }
    };

    loadProcedures();
  }, []);

  useEffect(() => {
    const loadAvailableTimes = async () => {
      if (!formData.servico_id || !formData.data) {
        setAvailableTimes([]);
        return;
      }

      setTimesLoading(true);
      try {
        const res = await axios.get('/api/agendamentos/disponiveis', {
          params: { data: formData.data, servico_id: formData.servico_id },
        });
        const times = res.data.horarios_disponiveis || [];
        setAvailableTimes(times);
        if (times.length > 0 && !times.includes(formData.hora)) {
          setFormData((prev) => ({ ...prev, hora: times[0] }));
        }
      } catch (err) {
        setAvailableTimes([]);
      } finally {
        setTimesLoading(false);
      }
    };

    loadAvailableTimes();
  }, [formData.data, formData.servico_id]);

  const validate = () => {
    const errs = {};
    if (!formData.nome.trim()) errs.nome = 'Por favor, informe seu nome.';
    if (!formData.telefone.trim()) {
      errs.telefone = 'Por favor, informe seu telefone com DDD.';
    } else if (formData.telefone.replace(/\D/g, '').length < 10) {
      errs.telefone = 'Telefone deve ter pelo menos 10 dígitos com DDD.';
    }
    if (!formData.servico_id) errs.servico_id = 'Selecione um procedimento.';
    if (!formData.data) errs.data = 'Selecione a data desejada.';
    if (!formData.hora) errs.hora = 'Selecione um horário disponível.';

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    setErrors({});

    const selectedProcedure = procedures.find((p) => Number(p.id) === Number(formData.servico_id));
    const servicoNome = formatProcedureName(selectedProcedure?.nome || 'Procedimento');
    const [year, month, day] = formData.data.split('-');
    const dataFormatada = `${day}/${month}/${year}`;

    const formatPhoneNumber = (value) => {
      const digits = (value || '').replace(/\D/g, '');
      if (digits.length === 11) {
        return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
      }
      if (digits.length === 10) {
        return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
      }
      return value;
    };

    const lines = [
      'Olá Fran!',
      '',
      `Nome: ${formData.nome.trim()}`,
      `Procedimento: ${servicoNome}`,
      `Data desejada: ${dataFormatada}`,
      `Horário de preferência: ${formData.hora}`,
    ];

    if (formData.observacoes.trim()) {
      lines.push(`Observações: ${formData.observacoes.trim()}`);
    }

    const text = lines.join('\n');
    const whatsappUrl = `https://wa.me/5567999359198?text=${encodeURIComponent(text)}`;

    try {
      const payload = {
        cliente: {
          nome: formData.nome.trim(),
          telefone: formData.telefone.trim(),
        },
        servico_id: Number(formData.servico_id),
        data_agendamento: formData.data,
        hora_inicio: formData.hora,
        observacoes: formData.observacoes.trim(),
      };

      await axios.post('/api/agendamentos/publico', payload);
      setSuccess(true);
      setSuccessDetails({
        nome: formData.nome,
        telefone: formData.telefone,
        data: dataFormatada,
        hora: formData.hora,
        servicoNome,
        whatsappUrl,
      });

      try {
        window.open(whatsappUrl, '_blank');
      } catch (_) {}
    } catch (err) {
      setErrors({
        submit: err.response?.data?.error || 'Não foi possível registrar a solicitação. Tente novamente.',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="site-page">
      <SiteHeader onNavigate={onNavigate} usuario={usuario} />

      <div className="site-sched-hero">
        <div className="site-container">
          <button
            type="button"
            className="site-sched-back-btn"
            onClick={() => onNavigate('site')}
          >
            ← Voltar para a Página Inicial
          </button>
          <h1>Agendamento Online</h1>
          <p>Escolha seu procedimento, selecione o dia e horário de sua preferência.</p>
        </div>
      </div>

      <div className="site-container site-sched-body">
        {success ? (
          <div className="site-sched-success-card">
            <span className="site-sched-success-icon" style={{ fontSize: '2.5rem', color: '#27ae60', fontWeight: 'bold' }}>✓</span>
            <h2>Solicitação Registrada com Sucesso!</h2>
            <p className="site-sched-success-text">
              Olá <strong>{successDetails?.nome}</strong>, sua solicitação para <strong>{successDetails?.servicoNome}</strong> no dia <strong>{successDetails?.data}</strong> às <strong>{successDetails?.hora}</strong> foi registrada na agenda da Fran!
            </p>
            <p className="site-sched-success-sub">
              Para confirmar seu horário, envie a mensagem no WhatsApp da Fran:
            </p>

            {successDetails?.whatsappUrl && (
              <div style={{ marginBottom: '28px' }}>
                <a
                  href={successDetails.whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="site-sched-whatsapp-btn"
                >
                  Abrir Conversa no WhatsApp com a Fran
                </a>
              </div>
            )}

            <div className="site-sched-success-actions">
              <button
                type="button"
                className="site-hero-btn-primary"
                onClick={() => {
                  setSuccess(false);
                  setFormData({
                    nome: '',
                    telefone: '',
                    servico_id: procedures[0]?.id || '',
                    data: new Date().toISOString().split('T')[0],
                    hora: '',
                    observacoes: '',
                  });
                }}
              >
                Fazer Outra Solicitação
              </button>
              <button
                type="button"
                className="site-hero-btn-secondary"
                onClick={() => onNavigate('site')}
              >
                Voltar para o Site
              </button>
            </div>
          </div>
        ) : (
          <div className="site-sched-card">
            {errors.submit && <div className="site-sched-error-banner">{errors.submit}</div>}

            <form onSubmit={handleSubmit} className="site-sched-form">
              {/* Informações Pessoais */}
              <div className="site-sched-section">
                <h3>1. Seus Dados de Contato</h3>
                <div className="site-sched-row">
                  <div className="site-sched-field">
                    <label>Seu Nome Completo *</label>
                    <input
                      type="text"
                      placeholder="Ex: Maria Silva"
                      value={formData.nome}
                      onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                      required
                    />
                    {errors.nome && <span className="site-field-error">{errors.nome}</span>}
                  </div>

                  <div className="site-sched-field">
                    <label>WhatsApp / Celular com DDD *</label>
                    <input
                      type="tel"
                      placeholder="(67) 99999-9999"
                      value={formData.telefone}
                      onChange={(e) => setFormData({ ...formData, telefone: formatPhoneInput(e.target.value) })}
                      required
                    />
                    {errors.telefone && <span className="site-field-error">{errors.telefone}</span>}
                  </div>
                </div>
              </div>

              {/* Procedimento e Data */}
              <div className="site-sched-section">
                <h3>2. Procedimento & Data</h3>
                <div className="site-sched-row">
                  <div className="site-sched-field">
                    <label>Procedimento Desejado *</label>
                    <select
                      value={formData.servico_id}
                      onChange={(e) => setFormData({ ...formData, servico_id: e.target.value })}
                    >
                      {procedures.map((p) => (
                        <option key={p.id} value={p.id}>
                          {formatProcedureName(p.nome)} {p.valor_padrao ? `— ${formatCurrency(p.valor_padrao)}` : ''}
                        </option>
                      ))}
                    </select>
                    {errors.servico_id && <span className="site-field-error">{errors.servico_id}</span>}
                  </div>

                  <div className="site-sched-field">
                    <label>Data Desejada *</label>
                    <input
                      type="date"
                      value={formData.data}
                      min={new Date().toISOString().split('T')[0]}
                      onChange={(e) => setFormData({ ...formData, data: e.target.value })}
                      required
                    />
                    {errors.data && <span className="site-field-error">{errors.data}</span>}
                  </div>
                </div>
              </div>

              {/* Horários Disponíveis */}
              <div className="site-sched-section">
                <h3>3. Horários Disponíveis</h3>
                {timesLoading ? (
                  <p className="site-sched-loading">Consultando horários livres na agenda...</p>
                ) : availableTimes.length === 0 ? (
                  <p className="site-sched-empty-times">
                    Nenhum horário livre encontrado para esta data. Por favor, escolha outro dia.
                  </p>
                ) : (
                  <div className="site-sched-times-grid">
                    {availableTimes.map((h) => (
                      <button
                        type="button"
                        key={h}
                        className={`site-time-chip ${formData.hora === h ? 'selected' : ''}`}
                        onClick={() => {
                          setFormData({ ...formData, hora: h });
                          setErrors((prev) => ({ ...prev, hora: '' }));
                        }}
                      >
                        {h}
                      </button>
                    ))}
                  </div>
                )}
                {errors.hora && <span className="site-field-error">{errors.hora}</span>}
              </div>

              {/* Observações */}
              <div className="site-sched-section">
                <label>Observações ou preferências (opcional):</label>
                <textarea
                  rows="3"
                  placeholder="Ex: Primeira vez fazendo cílios, preferência por efeito natural..."
                  value={formData.observacoes}
                  onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                />
              </div>

              <button
                type="submit"
                className="site-hero-btn-primary site-sched-submit-btn"
                disabled={loading || timesLoading || availableTimes.length === 0}
              >
                {loading ? 'Registrando solicitação...' : 'Solicitar Horário no WhatsApp'}
              </button>
            </form>
          </div>
        )}
      </div>

      <SiteFooter onNavigate={onNavigate} usuario={usuario} />
    </div>
  );
}
